
import { useEffect, useState } from "react";
import type {
  Canvas,
  FabricObject,
  FabricImage,
  Textbox,
} from "fabric";

import { loadFonts } from "../fonts";

import {
  documentColors,
  imageRadius,
  isLine,
  makeQr,
  readCrop,
  setCropAspect,
  setCropZoom,
  setIconColor,
  setImageRadius,
  setTextColor,
  textColor,
} from "../fabricKit";

import { Icon } from "../Icon";
import { ColorPicker, NumberStepper, Popover, Slider } from "./Controls";

import { FontSelect } from "./Panels";

import type { Features } from "../../../types/studioTypes";

interface StudioEditor {
  selected: FabricObject | null;

  /**
   * kindOf(selected) can return null when nothing is selected.
   */
  kind: string | null;

  canvas: Canvas | null;

  /**
   * The toolbar only needs width/height from the design.
   */
  design: {
    width: number;
    height: number;
  } | null;

  /**
   * The toolbar only needs colors from the brand.
   */
  brand: {
    colors?: string[];
  };

  /**
   * Your real editor stores crop as an object/null.
   */
  crop: {
    id?: string;
  } | null;

  cropImg?: FabricImage | null;

  /**
   * Actual editor state uses string.
   */
  panel: string;

  painting?: boolean;

  busy: string | null;

  features: Features;

  setBackground: (color: string) => void;

  setPanel: (panel: string) => void;

  setProp: (props: Record<string, unknown>) => void;

  each: (
    fn: (obj: FabricObject) => void,
    filter?: (obj: FabricObject) => boolean,
  ) => void;

  group: () => void;

  ungroup: () => void;

  copyStyle: () => void;

  duplicate: () => void;

  toggleLock: () => void;

  removeSelected: () => void;

  /**
   * The real implementation currently receives EObj.
   *
   * Using any here is intentional because FabricImage and your custom EObj
   * have incompatible Fabric generic types. The runtime object is still
   * checked/handled by the implementation.
   */
  removeImageBackground: (img: any) => Promise<void>;
  upscaleImage: (img: any, scale?: 2 | 4) => Promise<void>;
  magicLayers: (img: any) => Promise<void>;

  startCrop: (img?: any) => void;

  endCrop: () => void;

  changed: () => void;

  refresh: () => void;
}

interface ColorPickerSharedProps {
  brandColors: string[];
  docColors: () => string[];
}

// ---------------------------------------------------------------------------
// Shared Tailwind classes
// ---------------------------------------------------------------------------

const buttonBase = `
  inline-flex
  items-center
  justify-center
  gap-1.5
  whitespace-nowrap
  rounded-lg
  border
  border-st-line
  bg-white
  px-2.5
  py-1.5
  text-xs
  font-semibold
  text-st-ink
  transition
  hover:bg-st-paper
  focus-visible:outline
  focus-visible:outline-2
  focus-visible:outline-offset-2
  focus-visible:outline-[rgba(11,122,90,0.45)]
  disabled:cursor-not-allowed
  disabled:opacity-40
`;

const smallButton = `
  ${buttonBase}
  h-8
`;

const iconButton = `
  inline-flex
  h-8
  w-8
  shrink-0
  cursor-pointer
  items-center
  justify-center
  rounded-lg
  border
  border-transparent
  bg-transparent
  p-0
  text-st-muted
  transition
  hover:bg-st-paper
  hover:text-st-ink
  focus-visible:outline
  focus-visible:outline-2
  focus-visible:outline-offset-2
  focus-visible:outline-[rgba(11,122,90,0.45)]
  disabled:cursor-not-allowed
  disabled:opacity-40
`;

const accentButton = `
  ${smallButton}
  border-st-green
  bg-st-green
  text-white
  hover:bg-st-green-dark
`;

const accentSoftButton = `
  ${smallButton}
  border-st-green-soft
  bg-st-green-soft
  text-st-green
  hover:border-st-green
  hover:bg-st-green-soft
`;

const dangerIconButton = `
  ${iconButton}
  text-st-red
  hover:bg-[rgba(176,53,31,0.08)]
  hover:text-st-red
`;

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface ContextToolbarProps {
  ed: StudioEditor;
}

/**
 * The bar above the page:
 * tools for whatever is selected, similar to Canva's top toolbar.
 */
export function ContextToolbar({ ed }: ContextToolbarProps) {
  const { selected: o, kind, canvas: c, design } = ed;

  const brandColors = ed.brand.colors || [];

  const docColors = () => (c ? documentColors(c) : []);

  const cp: ColorPickerSharedProps = {
    brandColors,
    docColors,
  };

  if (ed.crop && ed.cropImg) {
    return <CropBar ed={ed} img={ed.cropImg} />;
  }

  return (
    <div
      className="
        flex
        min-h-[56px]
        min-w-0
        w-full
        items-center
        gap-2
        border-b
        border-st-line
        bg-white
        px-3
        py-2
        no-scrollbar
      "
      role="toolbar"
      aria-label="Selection tools"
    >
      {!o && design && c && (
        <>
          <ColorPicker
            value={
              typeof c.backgroundColor === "string"
                ? c.backgroundColor
                : "#ffffff"
            }
            onChange={(v) => ed.setBackground(v || "#ffffff")}
            label="Page colour"
            {...cp}
          />

          <span
            className="
              min-w-0
              truncate
              text-xs
              text-st-muted
            "
          >
            {design.width} × {design.height} px. Select something to edit it,
            or press T to add text.
          </span>
        </>
      )}

      {!design && (
        <span className="text-xs text-st-muted">
          Loading…
        </span>
      )}

      {kind === "text" && o && (
        <TextTools
          ed={ed}
          t={o as Textbox}
          cp={cp}
        />
      )}

      {kind === "image" && o && (
        <ImageTools
          ed={ed}
          img={o as FabricImage}
        />
      )}

      {kind === "frame" && (
        <button
          type="button"
          className={accentSoftButton}
          onClick={() => ed.setPanel("photos")}
        >
          <Icon name="image" size={16} />
          Add photo
        </button>
      )}

      {kind === "shape" && o && (
        <ShapeTools
          ed={ed}
          s={o}
          cp={cp}
        />
      )}

      {kind === "drawing" && o && (
        <>
          <ColorPicker
            value={
              typeof o.stroke === "string"
                ? o.stroke
                : undefined
            }
            onChange={(v) =>
              ed.setProp({
                stroke: v,
              })
            }
            label="Colour"
            {...cp}
          />

          <Popover label="Thickness">
            <Slider
              label="Thickness"
              value={Math.round(o.strokeWidth ?? 1)}
              min={1}
              max={120}
              onChange={(v) =>
                ed.setProp({
                  strokeWidth: v,
                })
              }
            />
          </Popover>
        </>
      )}

      {kind === "icon" && o && (
        <>
          <ColorPicker
            value={(o as any).icon?.color}
            onChange={(v) => {
              ed.each(
                (x) => {
                  setIconColor(
                    x,
                    v || "#10231e",
                  );
                },
                (x) =>
                  (x.type || "").toLowerCase() ===
                    "group" &&
                  !!(x as any).icon,
              );

              ed.changed();
              ed.refresh();
            }}
            label="Icon colour"
            {...cp}
          />

          <Popover label="Line weight">
            <Slider
              label="Line weight"
              value={Math.round(
                ((o as any).icon?.strokeWidth || 2) * 10,
              ) / 10}
              min={0.5}
              max={4}
              step={0.25}
              onChange={(v) => {
                ed.each((x) => {
                  (
                    (x as any)._objects || []
                  ).forEach((k: any) =>
                    k.set?.({
                      strokeWidth: v,
                    }),
                  );

                  (x as any).icon = {
                    ...(x as any).icon,
                    strokeWidth: v,
                  };

                  x.dirty = true;
                });

                ed.changed();
                ed.refresh();
              }}
            />
          </Popover>
        </>
      )}

      {kind === "qr" && o && (
        <QrTools
          ed={ed}
          g={o}
          cp={cp}
        />
      )}

      {kind === "multi" && o && (
        <>
          <button
            type="button"
            className={smallButton}
            onClick={ed.group}
            title="Group (Ctrl+G)"
          >
            <Icon name="group" size={16} />
            Group
          </button>

          {(o as any)
            .getObjects?.()
            ?.some(
              (x: FabricObject) =>
                (x.type || "").toLowerCase() ===
                "textbox",
            ) && (
            <button
              type="button"
              className={smallButton}
              onClick={() =>
                ed.setPanel("effects")
              }
            >
              Text effects
            </button>
          )}
        </>
      )}

      {kind === "group" && (
        <button
          type="button"
          className={smallButton}
          onClick={ed.ungroup}
          title="Ungroup (Ctrl+Shift+G)"
        >
          <Icon name="ungroup" size={16} />
          Ungroup
        </button>
      )}

      {o && (
        <div
          className="
            ml-auto
            flex
            shrink-0
            items-center
            gap-1
          "
        >
          <button
            type="button"
            className={`
              ${smallButton}
              ${
                ed.panel === "position"
                  ? "border-st-green bg-st-green-soft text-st-green"
                  : ""
              }
            `}
            onClick={() =>
              ed.setPanel("position")
            }
          >
            Position
          </button>

          {kind !== "multi" && (
            <button
              type="button"
              className={`
                ${iconButton}
                ${
                  ed.painting
                    ? "bg-st-green-soft text-st-green"
                    : ""
                }
              `}
              onClick={() => ed.copyStyle()}
              title="Copy style (Ctrl+Alt+C), then click another element"
              aria-label="Copy style"
            >
              <Icon name="paint" size={18} />
            </button>
          )}

          <Popover
            label={
              <Icon
                name="sparkle"
                size={18}
              />
            }
            title="Transparency"
            align="right"
            buttonClass={iconButton}
          >
            <Slider
              label="Transparency"
              value={Math.round(
                (o.opacity ?? 1) * 100,
              )}
              min={0}
              max={100}
              onChange={(v) =>
                ed.setProp({
                  opacity: v / 100,
                })
              }
              suffix="%"
            />
          </Popover>

          <button
            type="button"
            className={iconButton}
            onClick={ed.duplicate}
            title="Duplicate (Ctrl+D)"
            aria-label="Duplicate"
          >
            <Icon name="copy" size={18} />
          </button>

          {kind !== "multi" && (
            <button
              type="button"
              className={iconButton}
              onClick={() =>
                ed.toggleLock()
              }
              title={
                (o as any).locked
                  ? "Unlock"
                  : "Lock (Ctrl+Shift+L)"
              }
              aria-label={
                (o as any).locked
                  ? "Unlock"
                  : "Lock"
              }
            >
              <Icon
                name={
                  (o as any).locked
                    ? "lock"
                    : "unlock"
                }
                size={18}
              />
            </button>
          )}

          <button
            type="button"
            className={dangerIconButton}
            onClick={ed.removeSelected}
            disabled={!!(o as any).locked}
            title="Delete"
            aria-label="Delete"
          >
            <Icon name="trash" size={18} />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Text
// ---------------------------------------------------------------------------

interface TextToolsProps {
  ed: StudioEditor;
  t: Textbox;
  cp: ColorPickerSharedProps;
}

function TextTools({
  ed,
  t,
  cp,
}: TextToolsProps) {
  const bold =
    Number(t.fontWeight) >= 600 ||
    t.fontWeight === "bold";

  const upper =
    t.text === t.text?.toUpperCase() &&
    /[a-z]/i.test(t.text || "");

  const currentAlign =
    (t.textAlign as
      | "left"
      | "center"
      | "right"
      | "justify") || "left";

  const nextAlign = {
    left: "center",
    center: "right",
    right: "justify",
    justify: "left",
  } as const;

  const nextTextAlign =
    nextAlign[currentAlign] || "center";

  const alignIcon = {
    left: "alignLeft",
    center: "alignCenter",
    right: "alignRight",
    justify: "alignCenter",
  } as const;

  return (
    <>
      <FontSelect
        value={t.fontFamily || ""}
        style={{
          fontFamily: `"${t.fontFamily}"`,
          width: 168,
        }}
        onChange={async (f: string) => {
          await loadFonts([f]);

          ed.setProp({
            fontFamily: f,
          });
        }}
      />

      <NumberStepper
        value={Math.round(
          (t.fontSize ?? 16) *
            (t.scaleY || 1),
        )}
        min={6}
        max={800}
        onChange={(v) =>
          ed.setProp({
            fontSize:
              v / (t.scaleY || 1),
          })
        }
        label="Font size"
      />

      <ColorPicker
        value={textColor(t)}
        onChange={(v) =>
          ed.each(
            (x) => {
              if (
                x.type.toLowerCase() !==
                "textbox"
              ) {
                return;
              }

              setTextColor(
                x as Textbox,
                v || "#10231e",
              );
            },
            (x) =>
              (x.type || "").toLowerCase() ===
              "textbox",
          )
        }
        label="Text colour"
        variant="letter"
        {...cp}
      />

      <div
        className="
          flex
          shrink-0
          rounded-lg
          border
          border-st-line
          bg-white
        "
      >
        <button
          type="button"
          aria-pressed={bold}
          onClick={() =>
            ed.setProp({
              fontWeight: bold
                ? 400
                : 700,
            })
          }
          title="Bold (Ctrl+B)"
          className={`
            h-8
            w-8
            border-0
            border-r
            border-st-line
            bg-transparent
            text-sm
            text-st-ink
            hover:bg-st-paper
            ${
              bold
                ? "bg-st-green-soft text-st-green"
                : ""
            }
          `}
        >
          <b>B</b>
        </button>

        <button
          type="button"
          aria-pressed={
            t.fontStyle === "italic"
          }
          onClick={() =>
            ed.setProp({
              fontStyle:
                t.fontStyle === "italic"
                  ? "normal"
                  : "italic",
            })
          }
          title="Italic"
          className={`
            h-8
            w-8
            border-0
            border-r
            border-st-line
            bg-transparent
            text-sm
            text-st-ink
            hover:bg-st-paper
            ${
              t.fontStyle === "italic"
                ? "bg-st-green-soft text-st-green"
                : ""
            }
          `}
        >
          <i>I</i>
        </button>

        <button
          type="button"
          aria-pressed={!!t.underline}
          onClick={() =>
            ed.setProp({
              underline:
                !t.underline,
            })
          }
          title="Underline"
          className={`
            h-8
            w-8
            border-0
            border-r
            border-st-line
            bg-transparent
            text-sm
            text-st-ink
            hover:bg-st-paper
            ${
              t.underline
                ? "bg-st-green-soft text-st-green"
                : ""
            }
          `}
        >
          <u>U</u>
        </button>

        <button
          type="button"
          aria-pressed={
            !!t.linethrough
          }
          onClick={() =>
            ed.setProp({
              linethrough:
                !t.linethrough,
            })
          }
          title="Strikethrough"
          className={`
            h-8
            w-8
            border-0
            bg-transparent
            text-sm
            text-st-ink
            hover:bg-st-paper
            ${
              t.linethrough
                ? "bg-st-green-soft text-st-green"
                : ""
            }
          `}
        >
          <s>S</s>
        </button>

        <button
          type="button"
          onClick={() =>
            ed.each(
              (x) => {
                const textObject =
                  x as Textbox;

                x.set({
                  text: upper
                    ? textObject.text?.toLowerCase()
                    : textObject.text?.toUpperCase(),
                });
              },
              (x) =>
                (x.type || "").toLowerCase() ===
                "textbox",
            )
          }
          title="Uppercase"
          className={`
            h-8
            w-8
            border-0
            border-l
            border-st-line
            bg-transparent
            text-st-ink
            hover:bg-st-paper
            ${
              upper
                ? "bg-st-green-soft text-st-green"
                : ""
            }
          `}
        >
          <Icon name="case" size={18} />
        </button>
      </div>

      <button
        type="button"
        className={iconButton}
        onClick={() =>
          ed.setProp({
            textAlign:
              nextTextAlign,
          })
        }
        title={`Alignment: ${t.textAlign}`}
        aria-label="Change alignment"
      >
        <Icon
          name={
            alignIcon[currentAlign] ||
            "alignCenter"
          }
          size={18}
        />
      </button>

      <Popover label="Spacing">
        <Slider
          label="Letter spacing"
          value={t.charSpacing || 0}
          min={-200}
          max={800}
          onChange={(v) =>
            ed.setProp({
              charSpacing: v,
            })
          }
        />

        <Slider
          label="Line spacing"
          value={Math.round(
            (t.lineHeight || 1.16) *
              100,
          )}
          min={50}
          max={250}
          onChange={(v) =>
            ed.setProp({
              lineHeight: v / 100,
            })
          }
          suffix="%"
        />
      </Popover>

      <button
        type="button"
        className={`
          ${smallButton}
          ${
            ed.panel === "effects"
              ? "border-st-green bg-st-green-soft text-st-green"
              : ""
          }
        `}
        onClick={() =>
          ed.setPanel("effects")
        }
      >
        Effects
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------
// Shapes and lines
// ---------------------------------------------------------------------------

interface ShapeToolsProps {
  ed: StudioEditor;
  s: FabricObject;
  cp: ColorPickerSharedProps;
}

function ShapeTools({
  ed,
  s,
  cp,
}: ShapeToolsProps) {
  const line = isLine(s);

  const dash = !s.strokeDashArray
    ? "solid"
    : (s.strokeDashArray[0] as number) <
        1
      ? "dotted"
      : "dashed";

  const setDash = (
    style:
      | "solid"
      | "dashed"
      | "dotted",
  ) => {
    const w = s.strokeWidth || 4;

    ed.setProp({
      strokeDashArray:
        style === "solid"
          ? null
          : style === "dotted"
            ? [0.1, w * 2]
            : [w * 3, w * 2],

      strokeLineCap: "round",
    });
  };

  return (
    <>
      {!line && (
        <ColorPicker
          value={
            typeof s.fill === "string"
              ? s.fill
              : "#0b7a5a"
          }
          onChange={(v) =>
            ed.setProp({
              fill: v || "transparent",
            })
          }
          label="Fill colour"
          allowNone
          {...cp}
        />
      )}

      <ColorPicker
        value={
          typeof s.stroke === "string"
            ? s.stroke
            : "#10231e"
        }
        onChange={(v) =>
          ed.setProp({
            stroke: v,
            strokeWidth:
              s.strokeWidth || 4,
          })
        }
        label={
          line
            ? "Line colour"
            : "Border colour"
        }
        variant={
          line ? "dot" : "ring"
        }
        {...cp}
      />

      <Popover
        label={
          line
            ? "Line style"
            : "Border style"
        }
      >
        <div
          className="
            flex
            w-full
            overflow-hidden
            rounded-lg
            border
            border-st-line
          "
        >
          {(
            [
              "solid",
              "dashed",
              "dotted",
            ] as const
          ).map((k) => (
            <button
              key={k}
              type="button"
              aria-pressed={
                dash === k
              }
              onClick={() =>
                setDash(k)
              }
              className={`
                flex-1
                border-0
                border-r
                border-st-line
                bg-white
                px-2.5
                py-2
                text-xs
                font-semibold
                text-st-ink
                last:border-r-0
                hover:bg-st-paper
                ${
                  dash === k
                    ? "bg-st-green-soft text-st-green"
                    : ""
                }
              `}
            >
              {k[0].toUpperCase() +
                k.slice(1)}
            </button>
          ))}
        </div>

        <Slider
          label={
            line
              ? "Thickness"
              : "Border weight"
          }
          value={Math.round(
            s.strokeWidth || 0,
          )}
          min={0}
          max={80}
          onChange={(v) =>
            ed.setProp({
              strokeWidth: v,
              stroke:
                typeof s.stroke ===
                "string"
                  ? s.stroke
                  : "#10231e",
            })
          }
        />

        {(s.type || "").toLowerCase() ===
          "rect" && (
          <Slider
            label="Corner rounding"
            value={Math.round(
              (s as any).rx || 0,
            )}
            min={0}
            max={Math.round(
              Math.min(
                s.width ?? 0,
                s.height ?? 0,
              ) / 2,
            )}
            onChange={(v) =>
              ed.setProp({
                rx: v,
                ry: v,
              })
            }
          />
        )}
      </Popover>

      <Popover
        label={
          <Icon
            name="flipH"
            size={16}
          />
        }
        title="Flip"
      >
        <div className="grid gap-2">
          <button
            type="button"
            className={smallButton}
            onClick={() =>
              ed.setProp({
                flipX: !s.flipX,
              })
            }
          >
            <Icon
              name="flipH"
              size={16}
            />
            Flip across
          </button>

          <button
            type="button"
            className={smallButton}
            onClick={() =>
              ed.setProp({
                flipY: !s.flipY,
              })
            }
          >
            <Icon
              name="flipV"
              size={16}
            />
            Flip down
          </button>
        </div>
      </Popover>
    </>
  );
}

// ---------------------------------------------------------------------------
// Photos
// ---------------------------------------------------------------------------

interface ImageToolsProps {
  ed: StudioEditor;
  img: FabricImage;
}

function ImageTools({
  ed,
  img,
}: ImageToolsProps) {
  const frameShape =
    (img as any).frame?.shape;

  const shaped =
    frameShape &&
    !["rect", undefined].includes(
      frameShape,
    );

  return (
    <>
      <button
        type="button"
        className={`
          ${accentSoftButton}
          ${
            ed.panel === "adjust"
              ? "border-st-green bg-st-green-soft"
              : ""
          }
        `}
        onClick={() =>
          ed.setPanel("adjust")
        }
      >
        <Icon
          name="sparkle"
          size={16}
        />
        Edit photo
      </button>

      <button
        type="button"
        className={smallButton}
        disabled={ed.busy === "removebg" || ed.busy === "upscale" || ed.busy === "magic"}
        onClick={() => ed.removeImageBackground(img)}
        title="Remove the background (the cut-out is saved to your Gallery)"
      >
        <Icon name="wand" size={16} />
        {ed.busy === "removebg" ? "Removing…" : "BG Remover"}
      </button>

      <button
        type="button"
        className={smallButton}
        disabled={ed.busy === "removebg" || ed.busy === "upscale" || ed.busy === "magic"}
        onClick={() => ed.magicLayers(img)}
        title="Split the subject from its background into two layers"
      >
        <Icon name="layers" size={16} />
        {ed.busy === "magic" ? "Separating…" : "Magic layers"}
      </button>

      <Popover
        label={
          <>
            <Icon name="resize" size={16} />
            {ed.busy === "upscale" ? "Upscaling…" : "Upscale"}
          </>
        }
        title="Make the picture sharper and bigger"
      >
        <div className="grid gap-2">
          <button
            type="button"
            className={smallButton}
            disabled={ed.busy === "removebg" || ed.busy === "upscale" || ed.busy === "magic"}
            onClick={() => ed.upscaleImage(img, 2)}
          >
            2× bigger
          </button>
          <button
            type="button"
            className={smallButton}
            disabled={ed.busy === "removebg" || ed.busy === "upscale" || ed.busy === "magic"}
            onClick={() => ed.upscaleImage(img, 4)}
          >
            4× bigger
          </button>
        </div>
      </Popover>

      <button
        type="button"
        className={smallButton}
        onClick={() =>
          ed.startCrop(img)
        }
        title="Crop (or double-click the photo)"
      >
        <Icon
          name="crop"
          size={16}
        />
        Crop
      </button>

      <Popover
        label={
          <>
            <Icon
              name="flipH"
              size={16}
            />
            Flip
          </>
        }
        title="Flip"
      >
        <div className="grid gap-2">
          <button
            type="button"
            className={smallButton}
            onClick={() =>
              ed.setProp({
                flipX: !img.flipX,
              })
            }
          >
            <Icon
              name="flipH"
              size={16}
            />
            Flip across
          </button>

          <button
            type="button"
            className={smallButton}
            onClick={() =>
              ed.setProp({
                flipY: !img.flipY,
              })
            }
          >
            <Icon
              name="flipV"
              size={16}
            />
            Flip down
          </button>
        </div>
      </Popover>

      {!shaped &&
        !(img as any)
          .isBackgroundImage && (
          <Popover label="Corners">
            <Slider
              label="Rounding"
              value={imageRadius(img)}
              min={0}
              max={Math.round(
                Math.min(
                  img.getScaledWidth(),
                  img.getScaledHeight(),
                ) / 2,
              )}
              onChange={(r) =>
                ed.each((x) => {
                  if (
                    x.type ===
                    "image"
                  ) {
                    setImageRadius(
                      x as FabricImage,
                      r,
                    );
                  }
                })
              }
            />
          </Popover>
        )}

      <button
        type="button"
        className={smallButton}
        onClick={() =>
          ed.setPanel("photos")
        }
      >
        <Icon
          name="image"
          size={16}
        />
        Replace
      </button>
    </>
  );
}

// ---------------------------------------------------------------------------
// Crop bar
// ---------------------------------------------------------------------------

const ASPECTS: readonly (
  readonly [string, number | null]
)[] = [
  ["Original", null],
  ["Square", 1],
  ["4:5", 4 / 5],
  ["16:9", 16 / 9],
  ["9:16", 9 / 16],
  ["1.91:1", 1.91],
];

interface CropBarProps {
  ed: StudioEditor;
  img: FabricImage;
}

function CropBar({
  ed,
  img,
}: CropBarProps) {
  const [zoom, setZoom] =
    useState(
      () => readCrop(img).zoom,
    );

  useEffect(() => {
    setZoom(
      readCrop(img).zoom,
    );
  }, [img]);

  const redraw = () => {
    img.setCoords();
    ed.canvas?.requestRenderAll();
  };

  return (
    <div
      className="
        flex
        min-h-[56px]
        w-full
        items-center
        gap-3
        overflow-x-auto
        border-b
        border-st-line
        bg-white
        px-3
        py-2
        no-scrollbar
      "
      role="toolbar"
      aria-label="Crop"
    >
      <span
        className="
          flex
          shrink-0
          items-center
          gap-1.5
          text-sm
          font-semibold
          text-st-ink
        "
      >
        <Icon
          name="crop"
          size={18}
        />
        Crop
      </span>

      <div className="w-[180px] shrink-0">
        <Slider
          label="Zoom"
          value={Math.round(
            zoom * 100,
          )}
          min={100}
          max={400}
          suffix="%"
          onChange={(v) => {
            setZoom(v / 100);
            setCropZoom(
              img,
              v / 100,
            );
            redraw();
          }}
          onCommit={ed.changed}
        />
      </div>

      <div
        className="
          flex
          shrink-0
          overflow-hidden
          rounded-lg
          border
          border-st-line
          bg-white
        "
      >
        {ASPECTS.map(
          ([label, ratio]) => (
            <button
              key={label}
              type="button"
              className="
                border-0
                border-r
                border-st-line
                bg-white
                px-2.5
                py-1.5
                text-xs
                font-semibold
                text-st-ink
                last:border-r-0
                hover:bg-st-paper
              "
              onClick={() => {
                setCropAspect(
                  img,
                  ratio,
                );

                setZoom(1);
                redraw();
                ed.changed();
              }}
            >
              {label}
            </button>
          ),
        )}
      </div>

      <span
        className="
          min-w-[180px]
          flex-1
          text-xs
          text-st-muted
        "
      >
        Drag the photo to choose
        what shows.
      </span>

      <div
        className="
          ml-auto
          flex
          shrink-0
          items-center
          gap-2
        "
      >
        <button
          type="button"
          className={smallButton}
          onClick={() => {
            setCropZoom(
              img,
              1,
            );
            setZoom(1);
            redraw();
            ed.changed();
          }}
        >
          Reset
        </button>

        <button
          type="button"
          className={accentButton}
          onClick={ed.endCrop}
        >
          <Icon
            name="check"
            size={16}
          />
          Done
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// QR codes
// ---------------------------------------------------------------------------

interface QrToolsProps {
  ed: StudioEditor;
  g: FabricObject;
  cp: ColorPickerSharedProps;
}

function QrTools({
  ed,
  g,
  cp,
}: QrToolsProps) {
  const [text, setText] =
    useState(
      (g as any).qr?.text || "",
    );

  useEffect(() => {
    setText(
      (g as any).qr?.text || "",
    );
  }, [g]);

  async function regenerate(
    patch: Partial<{
      text: string;
      dark: string;
      light: string;
    }>,
  ) {
    const c = ed.canvas;

    if (!c) return;

    const q = {
      ...(g as any).qr,
      ...patch,
    };

    if (!q.text) return;

    const fresh =
      await makeQr(
        q.text,
        1000,
        1000,
        q.dark,
        q.light,
      );

    const size =
      g.getScaledWidth();

    const k =
      size /
      (fresh.width || 1);

    fresh.set({
      left: g.left,
      top: g.top,
      angle: g.angle,
      scaleX: k,
      scaleY: k,
      id: (g as any).id,
      opacity: g.opacity,
    });

    const index =
      c.getObjects().indexOf(g);

    c.remove(g);
    c.insertAt(
      index,
      fresh,
    );
    c.setActiveObject(
      fresh,
    );

    ed.changed();
    ed.refresh();

    c.requestRenderAll();
  }

  return (
    <>
      <ColorPicker
        value={
          (g as any).qr?.dark
        }
        onChange={(v) =>
          regenerate({
            dark:
              v || "#10231e",
          })
        }
        label="QR colour"
        {...cp}
      />

      <ColorPicker
        value={
          (g as any).qr?.light
        }
        onChange={(v) =>
          regenerate({
            light:
              v || "#ffffff",
          })
        }
        label="QR background"
        {...cp}
      />

      <Popover
        label={
          <>
            <Icon
              name="link"
              size={16}
            />
            Edit link
          </>
        }
        width={300}
      >
        {(close: () => void) => (
          <>
            <input
              className="
                h-9
                w-full
                rounded-lg
                border
                border-st-line
                bg-white
                px-3
                text-sm
                text-st-ink
                outline-none
                placeholder:text-st-muted
                focus:border-st-green
                focus:ring-2
                focus:ring-[rgba(11,122,90,0.12)]
              "
              value={text}
              onChange={(e) =>
                setText(
                  e.target.value,
                )
              }
              aria-label="QR content"
            />

            <div
              className="
                mt-3
                flex
                justify-end
                gap-2
              "
            >
              <button
                type="button"
                className={accentButton}
                onClick={() => {
                  regenerate({
                    text,
                  });

                  close();
                }}
              >
                Update
              </button>
            </div>
          </>
        )}
      </Popover>
    </>
  );
}

